import React, {
  ReactNode,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import classNames from 'classnames';
import useQueryParams from 'hooks/useQueryParams';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { NUMBERS_ONLY } from 'validations/general';
import { useTranslation } from 'react-i18next';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import { Button, Input } from 'ncoded-component-library';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ListingCreationSelect from 'router/subrouters/ListingCreationWizard/components/ListingCreationSelect';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import { ListingQueryFilterParams } from 'router/subrouters/Dash/subrouters/hooks/useBusinessesFilters';
import {
  createText,
  MAX_RANGE_VAL,
  RangeOptionsCopies,
  translateInputToSelectValues,
} from './utils';
import { FIRST_PAGE } from 'data/global';
import utils from 'utils';
import { FilterActionWrapperProps } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper/FilterActionWrapper.component';

import './RangeFilterWrapper.styles.scss';
import './RangeFilterWrapper.styles.responsive.scss';

export type PriceRangeValue = { from?: number; to?: number };

export type PriceRanggeCustomApply = {
  value?: PriceRangeValue;
  onApply?: (value: PriceRangeValue) => void;
};

type RangeFilterWrapperProps = {
  className?: string;
  selectValues: {
    minValue: number;
    maxValue: number;
    copies: RangeOptionsCopies;
  };
  paramsKeywords: {
    fromKeyword: keyof ListingQueryFilterParams;
    toKeyword: keyof ListingQueryFilterParams;
  };
  triggerText: string;
  triggerIcon?: ReactNode;
  applyButtonText: string;
  maxRangeValue?: number;
  isEbidta?: boolean;
} & PriceRanggeCustomApply &
  Partial<FilterActionWrapperProps>;

const SUPOSED_MAX_VAL = 50000000;
const NO_MAX_VAL_STRING = 'No max';

const RangeFilterWrapper: React.FC<RangeFilterWrapperProps> = (props) => {
  const { t } = useTranslation();
  const {
    className,
    selectValues,
    paramsKeywords,
    triggerText,
    triggerIcon,
    applyButtonText,
    maxRangeValue = MAX_RANGE_VAL,
    isEbidta,
    value,
    onApply,
    ...rest
  } = props;
  const {
    minValue: selectMinVal,
    maxValue: selectMaxVal,
    copies: selectCopies,
  } = selectValues;
  const { fromKeyword, toKeyword } = paramsKeywords;

  const SELECT_TO_INPUT = useMemo(
    () => ({
      [selectCopies.UNDER_MIN]: { to: selectMinVal },
      [selectCopies.BETWEEN_VALUES]: { from: selectMinVal, to: selectMaxVal },
      [selectCopies.ABOVE_MAX]: { from: selectMaxVal },
    }),
    [selectCopies, selectMaxVal, selectMinVal],
  );

  const selectOptions = useMemo(
    () =>
      Object.values(selectCopies).map((copy) => ({
        label: String(copy),
        value: String(copy),
      })),
    [selectCopies],
  );

  const { params, setQueryParam, removeQueryParam } =
    useQueryParams<ListingQueryFilterParams>();

  const [from, to] = value
    ? [value?.from, value?.to]
    : [params[fromKeyword], params[toKeyword]];

  const translateInputToSelect = useCallback(
    (from: number | string, to: number | string) =>
      translateInputToSelectValues(
        from,
        to,
        selectMinVal,
        selectMaxVal,
        selectCopies,
      ),
    [selectCopies, selectMaxVal, selectMinVal],
  );

  const createInputState = useCallback(
    (from, to) => ({
      to: to ? +to : maxRangeValue,
      from: from ? +from : 0,
    }),
    [maxRangeValue],
  );

  const [showSelect, setShowSelect] = useState(true);
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const defaultVal = useMemo(
    () => translateInputToSelect(from, to) || '',
    [from, to, translateInputToSelect],
  );
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [currentState, setCurrentState] = useState(defaultVal);
  const [inputState, setInputState] = useState(createInputState(from, to));

  const applyQueryFilter = useCallback(
    (key: string, value: string) =>
      !value
        ? removeQueryParam(key as any)
        : setQueryParam(key as any, value.toString()),
    [removeQueryParam, setQueryParam],
  );

  const applyRangeFilter = useCallback(
    (from, to) => {
      if (onApply) {
        onApply?.({ from, to });
      } else {
        applyQueryFilter(fromKeyword, from);
        applyQueryFilter(toKeyword, to);
        applyQueryFilter('page', FIRST_PAGE);
      }

      dropdown.setIsOpen(false);
    },
    [toKeyword, dropdown, fromKeyword, onApply, applyQueryFilter],
  );

  const onSelectFilterChange = useCallback(
    (value: string) => {
      setCurrentState(value);
      const { from, to } = SELECT_TO_INPUT[value];
      setInputState(createInputState(from, to));
    },
    [SELECT_TO_INPUT, createInputState],
  );

  const classes = classNames('range-filter-wrapper', className);

  const triggerTitle = useMemo(() => {
    return from || to ? createText(from, to) : triggerText;
  }, [from, to, triggerText]);

  const error = useMemo(() => {
    const { from, to } = inputState || { from: '', to: '' };

    return (from && +from !== 0 && !NUMBERS_ONLY.test(String(from))) ||
      (to && +to !== 0 && !NUMBERS_ONLY.test(String(to)))
      ? t('priceValuesMustBeNumber')
      : +from >= +to
      ? t('minValErrorText')
      : '';
  }, [inputState, t]);

  useEffect(() => {
    if (!toKeyword || !fromKeyword) return;

    setInputState(createInputState(from, to));
  }, [to, from, toKeyword, fromKeyword, createInputState]);

  useEffect(() => {
    if (error) {
      setButtonDisabled(true);
      return;
    }

    if (+to !== +inputState.to || +from !== +inputState.from) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [error, from, inputState, to]);

  useEffect(() => {
    setCurrentState(translateInputToSelect(inputState.from, inputState.to));
  }, [inputState, translateInputToSelect]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      portalClassName="range-filter-wrapper__portal"
      icon={triggerIcon}
      trigerTitle={triggerTitle}
      onClose={() => {
        setInputState(createInputState(from, to));
      }}
      {...rest}
    >
      <div className="range-filter-wrapper__content-container">
        <div
          className={classNames(
            'range-filter-wrapper__content-container__selection-cards',
            {
              shown: showSelect,
            },
          )}
        >
          <div className="range-filter-wrapper__content-container__heading">
            <p>{t('selectARange')}</p>
            <Button
              onClick={() => {
                setShowSelect(false);
              }}
              className="as-link"
            >
              {t('chooseSpecificRange')}
              <ArrowrightIcon />
            </Button>
          </div>

          <ListingCreationSelect
            value={currentState}
            columns={3}
            options={selectOptions}
            onChange={onSelectFilterChange}
            styleType="secondary"
          />
        </div>
        <div
          className={classNames(
            'range-filter-wrapper__content-container__inputs',
            {
              shown: !showSelect,
              error,
            },
          )}
        >
          <div className="range-filter-wrapper__content-container__heading">
            <p>{t('chooseSpecificRange')}</p>
            <Button
              onClick={() => {
                setShowSelect(true);
              }}
              className="as-link"
            >
              <ArrowleftIcon />
              {t('selectARange')}
            </Button>
          </div>
          <div className="range-filter-wrapper__content-container__inputs__wrapper">
            <div>
              <label>{t(isEbidta ? 'minAmount' : 'minPrice')}</label>
              <Input
                prefixNode="$"
                value={utils.getDotedNumber(inputState.from)}
                onChange={(ev) => {
                  setInputState((old) => ({
                    ...old,
                    from: utils.getIntegerFromDottedNumber(
                      ev.target.value.replace(/\D/, ''),
                    ),
                  }));
                }}
              />
            </div>
            <div>
              <label>{t(isEbidta ? 'maxAmount' : 'maxPrice')}</label>

              <Input
                prefixNode="$"
                placeholder={NO_MAX_VAL_STRING}
                value={
                  isEbidta &&
                  (inputState.to >= SUPOSED_MAX_VAL || inputState.to === 0)
                    ? ''
                    : utils.getDotedNumber(inputState.to)
                }
                onChange={(ev) => {
                  if (+ev.target.value === 0 || !ev.target.value) {
                    setInputState((old) => ({
                      ...old,
                      to: MAX_RANGE_VAL,
                    }));
                    return;
                  }

                  ev.target.value &&
                    setInputState((old) => ({
                      ...old,
                      to: utils.getIntegerFromDottedNumber(
                        ev.target.value.replace(/\D/, ''),
                      ),
                    }));
                }}
              />
            </div>
          </div>
          {error && <small>{error}</small>}
        </div>
      </div>

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={buttonDisabled}
          onClick={() => applyRangeFilter(inputState.from, inputState.to)}
        >
          {applyButtonText}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default RangeFilterWrapper;
